import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { ROUTES } from '@constants/routes';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login({
        username: formData.username,
        password: formData.password,
      });
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-white antialiased min-h-screen flex flex-col relative overflow-hidden transition-colors duration-300">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Abstract glowing orb */}
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-brand/10 blur-[100px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/5 blur-[120px]"></div>
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
      </div>

      <div className="layout-container flex h-full grow flex-col justify-center items-center px-4 py-12 relative z-10 w-full">
        {/* Main Card */}
        <div className="w-full max-w-[480px] flex flex-col bg-white dark:bg-[#1c212c] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">

          {/* Logo & Header Section */}
          <div className="px-8 pt-10 pb-6 flex flex-col items-center text-center">
            <div className="size-12 bg-brand/10 rounded-xl flex items-center justify-center mb-6 text-brand">
              <ShieldCheckIcon className="size-8 text-brand" />
            </div>

            <Link to={ROUTES.HOME} className="hover:text-brand transition-colors px-3 py-2 rounded-md text-sm font-medium text-white">
              <h1 className="text-3xl font-bold leading-tight tracking-tight mb-2 text-slate-900 dark:text-white">
                FortifyTech
              </h1>
            </Link>

            <p className="text-slate-500 dark:text-slate-400 text-base font-normal leading-normal">Secure access to your FortifyTech dashboard.</p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit} className="px-8 pb-10 space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm text-center">
                {error}
              </div>
            )}

            {/* Email/Username Field */}
            <div className="flex flex-col gap-2">
              <label className="text-slate-700 dark:text-slate-300 text-sm font-medium leading-normal" htmlFor="username">
                Username or Email
              </label>
              <div className="relative">
                <input
                  id="username"
                  type="text"
                  placeholder="admin or name@fortifytech.org.pe"
                  className="flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-slate-900 dark:text-white focus:outline-0 focus:ring-2 focus:ring-brand/50 border border-slate-300 dark:border-[#3b4354] bg-slate-50 dark:bg-[#11141a] focus:border-brand h-12 placeholder:text-slate-400 dark:placeholder:text-[#9da6b9] px-4 text-base font-normal leading-normal transition-all"
                  value={formData.username}
                  onChange={handleChange('username')}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-slate-700 dark:text-slate-300 text-sm font-medium leading-normal" htmlFor="password">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm font-medium text-brand hover:text-brand/80 transition-colors">
                  Forgot Password?
                </Link>
              </div>
              <div className="relative flex w-full items-stretch rounded-lg">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className="flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg rounded-r-none text-slate-900 dark:text-white focus:outline-0 focus:ring-2 focus:ring-brand/50 border border-slate-300 dark:border-[#3b4354] bg-slate-50 dark:bg-[#11141a] focus:border-brand h-12 placeholder:text-slate-400 dark:placeholder:text-[#9da6b9] px-4 border-r-0 text-base font-normal leading-normal transition-all z-10"
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex items-center justify-center px-4 rounded-r-lg border border-l-0 border-slate-300 dark:border-[#3b4354] bg-slate-50 dark:bg-[#11141a] text-slate-400 dark:text-[#9da6b9] hover:text-slate-600 dark:hover:text-white transition-colors cursor-pointer z-0"
                >
                  {showPassword ? <EyeSlashIcon className="size-5" /> : <EyeIcon className="size-5" />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-brand hover:bg-brand/90 text-white text-base font-bold leading-normal tracking-[0.015em] shadow-glow transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing In...
                </span>
              ) : (
                <span className="truncate">Sign In</span>
              )}
            </button>

            {/* Divider */}
            <div className="relative flex py-2 items-center">
              <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
              <span className="flex-shrink mx-4 text-slate-400 text-xs font-medium uppercase tracking-wider">Or continue with</span>
              <div className="flex-grow border-t border-slate-200 dark:border-slate-700"></div>
            </div>

            {/* Social Login Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <button type="button" className="flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#252b36] hover:bg-slate-50 dark:hover:bg-[#2f3642] transition-colors">
                <img alt="Google" className="size-5" src="https://lh3.googleusercontent.com/aida-public/AB6AXuATVhd3470Y1sZhBJStGf42cHGYpm-IEDPTtelga4wj95mPBe8AqBN1cD1tL6HOD4WKyJRwnImdtqrXOoQK9vBjeMOzW7JzIGJ3KjpXHd720vM2cQmih0gVfTAnwQHOpbWbWcxvtAsyVNV51_OpJ5iRJOddZSssyqLHT0Cz78yNT8JSbKJLC32JxyeTs7T0njxSOHd5Bucbmrbk_oYfwLwBDzrcopVo6-ZZmd-Uehh8Nscww8FQwcmNot6la4CNWsZKIL5wQ58No0w" />
                <span className="text-sm font-medium text-slate-700 dark:text-white">Google</span>
              </button>
              <button type="button" className="flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#252b36] hover:bg-slate-50 dark:hover:bg-[#2f3642] transition-colors">
                <img alt="Microsoft" className="size-5" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDNsJDEGt2AGFljYdUtVsQypx82qi20TWkifjqgy33vd5n6kvp5tJcRLwfRvKI73_x0qD1BMjlYMkaEVcXtMOF378hyMx5mQOZDsu0I-202XyePLEh5qYfvQtwwHqED-PWnoWrBxu8Xt2iyO4V1Gmsr3hgXa7DAYsG_E0rV2ArV2tfDxo3b8728yjUm5YZzdoxw8gxSunjobZaKbTYbvgCAfNoKlGFHxKhMmqPWzjyAtDo1PhEXR6BAP9mLW1-FY12ygUmLL6_y_xY" />
                <span className="text-sm font-medium text-slate-700 dark:text-white">Microsoft</span>
              </button>
            </div>

            {/* Footer Sign Up */}
            <div className="text-center pt-2">
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Don&apos;t have an account? <Link to="/register" className="text-brand font-bold hover:underline">Sign Up</Link>
              </p>
            </div>
          </form>

          {/* Demo Credentials (Keep for usability) */}
          <div className="px-8 pb-4">
            <div className="p-3 bg-blue-50 dark:bg-gray-800/50 rounded-lg text-xs text-center border border-blue-100 dark:border-gray-700">
              <strong>Demo Credentials:</strong><br />
              User: admin<br />
              Pass: SuperSecureP@ssw0rd!
            </div>
          </div>
        </div>

        {/* Bottom Links */}
        <div className="mt-8 flex gap-6 text-sm text-slate-500 dark:text-slate-400 font-medium z-10">
          <Link to="/privacy" className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors">Terms of Service</Link>
          <Link to="/support" className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};